import React, { useEffect, useRef, useState } from 'react'
import { useAuthContext } from '../../hooks/useAuthContext'
import { previewService } from '../../services/previewService'
import { processDataService } from '../../services/processDataService'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  CircularProgress,
  Button,
  Collapse,
  Paper,
} from '@mui/material'
import PagePreview from './components/PagePreview'
import { HighlightOff } from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import AdditionalNotes from './components/AdditionalNotes'
import { useResponsiveTypography } from '../../utility/responsiveTypography'
import Queries from './components/Queries'
import OverallComments from './components/OverallComments'
import { CURRENCY_COUNTRY_CODE, END_ACTIVITIES } from '../../utility/constants'
import { CURRENCY_ERROR } from '../../pages/DataEntryPage/utils/currency'

import './Style.css'
import ExportExcelData from '../Dashboard/ExportExcelData'

const DataPreview = ({
  processId,
  onClose,
  open,
  setLoading,
  onSubmit,
  routeTemplateId,
  isSubmission = false,
  isDashboardPreview = false,
  hideOverallComment = false,
}) => {
  const { heading, subheading } = useResponsiveTypography()

  const { user } = useAuthContext()
  const [previewData, setPreviewData] = useState(null)
  const [notifications, setNotifications] = useState([])
  const [expandedNotifications, setExpandedNotifications] = useState({})
  const VISIBLE_LI_COUNT = 3
  const prevHashRef = useRef('')

  const getPreviewData = async () => {
    // call api here
    const previewData = await previewService.getPreviewData(user, processId)
    return previewData
  }

  useEffect(() => {
    ;(async () => {
      const previewData = await getPreviewData(user, processId)
      setPreviewData(previewData)
    })()
  }, [processId])

  // bubble notification effect — runs when previewData is loaded
  useEffect(() => {
    if (!previewData) return

    const customViewFields =
      previewData.fields?.filter((field) => field.data_structure === 'custom-view') || []

    if (!customViewFields.length) return

    const customViewDataMap = customViewFields.reduce((acc, field) => {
      const tableData = previewData.data?.data?.[`f_${field.id}`]
      if (Array.isArray(tableData) && tableData.length) {
        acc[field.id] = tableData
      }
      return acc
    }, {})

    if (!Object.keys(customViewDataMap).length) return

    const currentHash = JSON.stringify(customViewDataMap)
    if (prevHashRef.current === currentHash) return
    prevHashRef.current = currentHash

    const checkChangeInPublishedFile = async () => {
      const { process_reference_num, status, process_template_id } = previewData.data

      const promises = Object.entries(customViewDataMap).map(([fieldId, tableData]) =>
        processDataService
          .bubbleNotification(user, {
            records: tableData,
            current_process_template_id: process_template_id,
            status,
            target: 'dms process',
            current_process_reference_num: process_reference_num,
            currentFieldId: Number(fieldId),
          })
          .catch((err) => {
            console.error(`bubbleNotification failed for field ${fieldId}:`, err)
            return null
          }),
      )

      const results = await Promise.all(promises)

      const allNotifications = results.flatMap((res) => {
        const notificationsData = res?.data?.notification || []
        return notificationsData.map((n, i) => ({
          ...n,
          open: true,
          id: Date.now() + i,
        }))
      })

      if (allNotifications.length) {
        setNotifications(allNotifications)
      }
    }

    checkChangeInPublishedFile()
  }, [previewData])

  // cleanup closed notifications after collapse animation
  useEffect(() => {
    const closed = notifications.filter((n) => !n.open)
    if (!closed.length) return
    const timer = setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.open))
    }, 300)
    return () => clearTimeout(timer)
  }, [notifications])

  const previewCurrency = previewData?.ndc.reduce((acc, item) => {
    // Use the field_template_id as key and look up the currency symbol from CURRENCY_COUNTRY_CODE
    acc[item.field_template_id] = CURRENCY_COUNTRY_CODE[item.currency_code] || CURRENCY_ERROR
    return acc
  }, {})

  const parseNotificationHtml = (html) => {
    const titleMatch = html.match(/<p>(.*?)<\/p>/)
    const liMatches = [...html.matchAll(/<li>(.*?)<\/li>/g)]
    return {
      title: titleMatch?.[1] ?? '',
      items: liMatches.map((m) => m[1]),
    }
  }

  const toggleLiExpand = (id) => {
    setExpandedNotifications((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  return (
    <Dialog
      className="preview-root"
      onClose={onClose}
      open={open}
      style={{ overflowY: 'scroll', margin: 64, borderRadius: 8 }}
      fullScreen
    >
      {previewData == null ? (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              width: '100%',
              minWidth: 600,
              maxWidth: 800,
              padding: '20px',
              height: 100,
            }}
          >
            <CircularProgress color="primary" size={24} />
            <Typography>Loading Preview</Typography>
          </Box>
        </Box>
      ) : (
        <Box
          className="preview-page"
          style={{
            padding: '0px 24px 24px 24px',
            overflowX: 'hidden',
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <Box
            className="preview-header"
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Box>
              <Typography
                className="textPrimary fontWeight700"
                variant="h6"
                sx={{ fontSize: heading }}
              >
                {previewData?.data?.activity_template_name} (Preview)
              </Typography>

              <Typography className="textprimaryblue fontWeight600" sx={{ fontSize: subheading }}>
                {previewData?.data.prefix}-{previewData?.data.process_reference_num} (version:{' '}
                {previewData?.data?.version})
              </Typography>
            </Box>
            <Box>
              {!END_ACTIVITIES.includes(previewData?.data?.activity_type) && (
                <ExportExcelData processId={processId} />
              )}
              <IconButton onClick={() => onClose()}>
                <HighlightOff />
              </IconButton>
            </Box>
          </Box>

          {/* Notifications — inline below header */}
          {notifications.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0px', mb: 2 }}>
              {notifications.map((n) => {
                const isDuplicate = n.key === 'dcr-duplicates'
                const { title, items } = parseNotificationHtml(n.html)
                const isExpanded = expandedNotifications[n.id] ?? false
                const visibleItems = isExpanded ? items : items.slice(0, VISIBLE_LI_COUNT)
                const hiddenLiCount = items.length - VISIBLE_LI_COUNT

                return (
                  <Collapse key={n.id} in={n.open} unmountOnExit>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        mb: '8px',
                        padding: '5px 14px',
                        borderRadius: '6px',
                        borderLeft: `4px solid ${isDuplicate ? '#854F0B' : '#3B6D11'}`,
                        backgroundColor: isDuplicate ? '#FAEEDA' : '#EAF3DE',
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: '100%',
                        boxSizing: 'border-box',
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: '13px',
                          lineHeight: 1.5,
                          color: isDuplicate ? '#633806' : '#27500A',
                          flex: 1,
                        }}
                      >
                        {/* Title */}
                        <p style={{ margin: 0 }}>{title}</p>

                        {/* Li items */}
                        <ul style={{ margin: 0, paddingLeft: '16px' }}>
                          {visibleItems.map((item, index) => (
                            <li key={index} style={{ margin: 0 }}>
                              {item}
                            </li>
                          ))}
                        </ul>

                        {/* View more / less — only if li > 3 */}
                        {items.length > VISIBLE_LI_COUNT && (
                          <Box
                            onClick={() => toggleLiExpand(n.id)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              mt: '4px',
                              cursor: 'pointer',
                              width: 'fit-content',
                              color: isDuplicate ? '#633806' : '#27500A',
                              fontSize: '12px',
                              fontWeight: 500,
                              opacity: 0.85,
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {isExpanded ? (
                              <>
                                <ExpandLessIcon sx={{ fontSize: 14 }} />
                                View less
                              </>
                            ) : (
                              <>
                                <ExpandMoreIcon sx={{ fontSize: 14 }} />
                                {`View ${hiddenLiCount} more`}
                              </>
                            )}
                          </Box>
                        )}
                      </Box>

                      <IconButton
                        size="small"
                        onClick={() =>
                          setNotifications((prev) =>
                            prev.map((item) =>
                              item.id === n.id ? { ...item, open: false } : item,
                            ),
                          )
                        }
                        sx={{
                          color: isDuplicate ? '#633806' : '#27500A',
                          padding: '0px',
                          flexShrink: 0,
                          marginTop: '2px',
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Box>
                  </Collapse>
                )
              })}
            </Box>
          )}
          {previewData?.pages
            ?.sort((a, b) => a.sequence - b.sequence)
            ?.map((page, index) => {
              const fields = previewData?.fields.filter((it) => it.page_template_id == page.id)
              return (
                <PagePreview
                  key={page.id}
                  index={index}
                  counts={previewData?.pages?.length}
                  page={page}
                  fields={fields}
                  data={previewData?.data}
                  listData={previewData?.listData}
                  dynamicListData={previewData?.dynamicListData}
                  tableSubfieldMap={previewData.tableSubfieldMap}
                  tableSequenceAndHeaders={previewData.tableSequenceAndHeaders}
                  activityType={previewData.data.activity_type}
                  previewCurrency={previewCurrency}
                  processId={processId}
                  isPreview={true}
                  isDashboardPreview={isDashboardPreview}
                  defaultCurrency={previewData?.companyDetails?.default_currency_code}
                  externalValues={previewData?.externalValues}
                  fieldMappings={previewData?.fieldMappings}
                  fieldMetadata={previewData?.fieldMetadata}
                />
              )
            })}
          <Queries queries={previewData?.queries} />
          <AdditionalNotes
            additionalNotes={previewData?.additionalNotes}
            activityType={previewData?.data.activity_type}
          />
          {isSubmission && !hideOverallComment && (
            <OverallComments
              setLoading={setLoading}
              onSubmit={onSubmit}
              routeTemplateId={routeTemplateId}
            />
          )}
          {isSubmission && hideOverallComment && (
            <Box style={{ marginTop: '16px' }}>
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  onSubmit && onSubmit('.', [])
                  setLoading && setLoading(true)
                }}
              >
                Submit
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Dialog>
  )
}

export default DataPreview
